import React, { useState } from "react";
import Navigation from "../../shared/Navigation";
import { AiOutlineInfoCircle } from "react-icons/ai";
import { Helmet } from "react-helmet";

const EmergencyFundCalculator = () => {
  const [monthlyExpenses, setMonthlyExpenses] = useState(30000);
  const [months, setMonths] = useState(6);
  const [fundNeeded, setFundNeeded] = useState(180000);

  React.useEffect(() => {
    setFundNeeded(monthlyExpenses * months);
  }, [monthlyExpenses, months]);

  return (
    <>
      <Helmet>
        <title>
          Emergency Fund Calculator - How Much Emergency Fund Do You Need? |
          BuildMyWealth
        </title>
        <meta
          name="description"
          content="Calculate the ideal emergency fund for your family. Use BuildMyWealth's Emergency Fund Calculator to know how much to keep aside for emergencies."
        />
        <meta
          name="keywords"
          content="Emergency Fund Calculator, Emergency Savings, Liquid Fund, Financial Safety Net, BuildMyWealth"
        />
        <link
          rel="canonical"
          href="https://www.buildmywealth.net/calculators/emergency-fund"
        />
      </Helmet>
      <Navigation />
      <div className="w-full max-w-2xl mx-auto py-6 pt-28 px-2 sm:px-4">
        <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center mb-8 bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
          Emergency Fund Calculator
        </h1>
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-10">
          <div className="mb-6">
            <label className="block text-xs font-medium text-gray-700 mb-1 flex items-center gap-1">
              Average Monthly Expenses (₹)
              <span title="Include all essential expenses: rent, groceries, utilities, EMIs, etc.">
                <AiOutlineInfoCircle className="inline text-blue-400" />
              </span>
            </label>
            <input
              type="number"
              min="1000"
              value={monthlyExpenses}
              onChange={(e) => setMonthlyExpenses(Number(e.target.value))}
              className="block w-full px-2 py-1 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-sm"
            />
          </div>
          <div className="mb-6">
            <label className="block text-xs font-medium text-gray-700 mb-1 flex items-center gap-1">
              Number of Months to Cover
              <span title="Most experts recommend 3-6 months of expenses as emergency fund.">
                <AiOutlineInfoCircle className="inline text-blue-400" />
              </span>
            </label>
            <input
              type="number"
              min="1"
              max="24"
              value={months}
              onChange={(e) => setMonths(Number(e.target.value))}
              className="block w-full px-2 py-1 border border-gray-300 rounded-md shadow-sm focus:ring-purple-500 focus:border-purple-500 text-sm"
            />
          </div>
          <div className="mt-4 bg-gradient-to-br from-indigo-50 to-purple-50 p-4 rounded-lg text-center">
            <p className="text-lg font-semibold text-gray-700 mb-1">
              Recommended Emergency Fund
            </p>
            <p className="text-3xl font-bold text-indigo-600">
              ₹{fundNeeded.toLocaleString("en-IN")}
            </p>
            <p className="text-xs text-gray-500 mt-2">
              This is the amount you should keep in a liquid, safe place for
              emergencies.
            </p>
          </div>
          <div className="mt-4 text-center">
            <a
              href="https://wa.me/919616622620?text=Hi%2C%20I%20need%20help%20with%20SIP%20planning%20on%20BuildMyWealth.net"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-green-500 text-white px-4 py-2 rounded-md font-semibold hover:bg-green-600 inline-block"
              style={{ textDecoration: "none" }}
            >
              <span role="img" aria-label="WhatsApp" className="mr-2">
                💬
              </span>
              Get Expert Help on WhatsApp
            </a>
          </div>
          <div className="mt-8 text-sm text-gray-600">
            <b>What is an Emergency Fund?</b>
            <br />
            An emergency fund is a financial safety net for unexpected expenses
            such as medical emergencies, job loss, or urgent repairs. It should
            be easily accessible and kept in low-risk, liquid instruments like
            savings accounts or liquid mutual funds.
          </div>
          <div className="mt-8 bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl shadow p-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="font-semibold text-indigo-700">
                Recommended Fund Categories
              </span>
              <AiOutlineInfoCircle
                className="text-blue-400"
                title="Best options for parking your emergency fund."
              />
            </div>
            <ul className="list-disc pl-5 text-sm text-gray-700">
              <li>
                <span className="font-semibold">Liquid Mutual Funds:</span>{" "}
                Offer higher returns than savings accounts, with high liquidity
                and low risk.
              </li>
              <li>
                <span className="font-semibold">
                  Ultra Short Duration Funds:
                </span>{" "}
                Slightly higher returns than liquid funds, still very low risk
                and easy to redeem.
              </li>
              <li>
                <span className="font-semibold">Sweep-in Fixed Deposits:</span>{" "}
                Bank FDs with sweep-in facility for instant access if needed.
              </li>
              <li>
                <span className="font-semibold">Savings Account:</span> For
                instant access, though returns are lower.
              </li>
            </ul>
            <div className="text-xs text-gray-500 mt-2">
              Avoid locking your emergency fund in long-term or high-risk
              investments.
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default EmergencyFundCalculator;
